import { useTheme } from "@emotion/react";
import { Email } from "@mui/icons-material";
import {
  Alert,
  Box,
  Grid,
  InputAdornment,
  Link,
  TextField,
  Typography
} from "@mui/material";
import { ApplicationButton } from "../Components";
import { useMicrospecThemeLanguageContext } from "../Themes";

const LoginEmailIdentifier = ({
  identifier,
  error,
  loading,
  signIn,
  redirectTo,
  enableRegister = true,
  registerMode = false,
  onIdentifierChange,
  onSubmit
}) => {
  const { localizationProvider } = useMicrospecThemeLanguageContext();
  const theme = useTheme();

  // Generate gradients from theme colors
  const primaryGradient = `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`;

  return (
    <Box component="form" onSubmit={onSubmit} noValidate>
      {/* Email Input */}
      <TextField
        dir={"ltr"}
        margin="normal"
        required
        fullWidth
        id="identifier"
        name="identifier"
        label={localizationProvider.translateByType({
          type: "Internal",
          text: "emailAddress"
        })}
        type="email"
        autoComplete="email"
        autoFocus
        variant="outlined"
        value={identifier}
        onChange={onIdentifierChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Email color="action" />
            </InputAdornment>
          ),
        }}
        placeholder="example@email.com"
      />

      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          fontSize: '0.85rem',
        }}
      >
        {localizationProvider.translateByType({
          type: "Internal",
          text: "newAccountAutoCreationNoticeForEmail"
        })}
      </Typography>

      {error && (
        <Alert
          severity='error'
          sx={{
            mb: 2,
            borderRadius: 2,
            border: '1px solid rgba(211, 47, 47, 0.2)'
          }}
        >
          {typeof error === 'object' && error.message ? (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {error.message}
              </Typography>
              {error.showLoginLink && (
                <Link
                  href={redirectTo ? "/login?redirectTo=" + redirectTo : "/login"}
                  variant="body2"
                  sx={{
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {localizationProvider.translateByType({
                    type: "Internal",
                    text: "signIn"
                  })}
                </Link>
              )}
              {error.showRegisterLink && enableRegister && (
                <Link
                  href={registerMode ? 
                    (redirectTo ? "/login?redirectTo=" + redirectTo : "/login") : 
                    (redirectTo ? "/register?redirectTo=" + redirectTo : "/register")
                  }
                  variant="body2"
                  sx={{
                    textDecoration: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      textDecoration: 'underline'
                    }
                  }}
                >
                  {registerMode ? 
                    (localizationProvider.translateByType({
                      type: "Internal",
                      text: "signIn"
                    })) :
                    (localizationProvider.translateByType({
                      type: "Internal",
                      text: "signUp"
                    }))
                  }
                </Link>
              )}
            </Box>
          ) : (
            error || localizationProvider.translateByType({
              type: "Internal",
              text: "loginError"
            })
          )}
        </Alert>
      )}

      <ApplicationButton
        type="submit"
        fullWidth
        variant="contained"
        disabled={loading || !identifier.trim()}
        sx={{
          mt: 2,
          mb: 3,
          py: 1.5,
          borderRadius: 3,
          background: primaryGradient,
          boxShadow: `0 8px 32px ${theme.palette.primary.main}30`,
          fontSize: '1.1rem',
          fontWeight: 600,
          textTransform: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: `0 12px 40px ${theme.palette.primary.main}40`,
            background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
          },
          '&:disabled': {
            background: 'rgba(0, 0, 0, 0.12)',
            transform: 'none',
            boxShadow: 'none'
          }
        }}
      >
        {loading ? localizationProvider.translateByType({
          type: "Internal",
          text: "loggingInProgress"
        }) : signIn}
      </ApplicationButton>

      <Grid container spacing={8} sx={{ justifyContent: 'space-between' }}>
        <Grid item xs={6}>
          <Link
            textAlign={"end"}
            href={redirectTo ? "/forgot?redirectTo=" + redirectTo : "/forgot"}
            variant="body2"
            sx={{
              color: theme.palette.mode === 'dark' ? '#f0f0f0' : theme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 500,
              transition: 'color 0.3s ease',
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            <Typography>
              {localizationProvider.translateByType({
                type: "Internal",
                text: "forgotPassword"
              })}
            </Typography>
          </Link>
        </Grid>
        {enableRegister && (
          <Grid item xs={6}>
            <Link
              textAlign={"start"}
              href={registerMode ? 
                (redirectTo ? "/login?redirectTo=" + redirectTo : "/login") : 
                (redirectTo ? "/register?redirectTo=" + redirectTo : "/register")
              }
              variant="body2"
              sx={{
                color: theme.palette.mode === 'dark' ? '#f0f0f0' : theme.palette.primary.main,
                textDecoration: 'none',
                fontWeight: 500,
                transition: 'color 0.3s ease',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}
            >
              <Typography>
                {registerMode ? 
                  (localizationProvider.translateByType({
                    type: "Internal",
                    text: "signIn"
                  })) :
                  (localizationProvider.translateByType({
                    type: "Internal",
                    text: "createAccount"
                  })) 
                }
              </Typography>
            </Link>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default LoginEmailIdentifier;